// Fill with AI — the lines (Dean, Oct 2026): grouped per party and track in apply order, repeats collapsed with a count, a missing date fixed
// inline, one-offs and dependencies called out, and anything that cannot apply says why before Apply.
import { AlertTriangle, Check, CornerDownRight, GitBranch, Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { today } from "./mapShared";

const CONF = { high: "bg-emerald-50 text-emerald-800 border-emerald-300", medium: "bg-amber-50 text-amber-800 border-amber-300", low: "bg-slate-100 text-slate-600 border-slate-300" };

export function Sources({ s }) {
  if (!s || !Object.keys(s).length) return null;
  return (
    <div className="text-[10px] text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5" data-testid="map-ai-sources">
      <span>{s.notes} notes</span><span>{s.calendar} calendar items</span><span>{s.emails} emails</span><span>{s.files} files</span>
      <span title={(s.pdfs_failed || []).map((f) => `${f.name} — ${f.error}`).join("\n")}>{s.pdfs_read} PDF{s.pdfs_read === 1 ? "" : "s"} read{s.pdfs_failed?.length ? ` (${s.pdfs_failed.length} unreadable)` : ""}</span>
      <span>Airtable {s.airtable ? "row found" : "no row"}</span>
    </div>
  );
}

// consecutive repeats of the same recording (three "Open Adjournment" lines in a row) collapse into one group; every repeat is still applied
export const groupLines = (list) => list.reduce((acc, p) => {
  const last = acc[acc.length - 1];
  if (last && last.sig && last.sig === p.signature) last.lines.push(p);
  else acc.push({ key: p.id, sig: p.signature, lines: [p] });
  return acc;
}, []);

export function DateFix({ id, value, hints, onPick, compact }) {
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      <Input type="date" value={value || ""} max={today()} onChange={(e) => onPick(e.target.value)} className={`h-6 text-[11px] ${compact ? "w-32" : "w-36"}`} data-testid={`map-ai-date-${id}`} />
      {(hints || []).map((h) => (
        <button key={h.date + h.label} type="button" onClick={() => onPick(h.date)} className="h-6 px-2 rounded-full border border-amber-300 bg-amber-50 text-amber-900 text-[10px] hover:bg-amber-100 whitespace-nowrap" data-testid={`map-ai-hint-${id}`} title={h.date}>
          {h.label}
        </button>
      ))}
    </span>
  );
}

function Status({ pl, picked }) {
  if (!picked || !pl) return null;
  if (pl.blocker === "needs a date") return <span className="text-[9px] px-1.5 py-px rounded-full border bg-amber-50 text-amber-900 border-amber-300 flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5" /> needs a date</span>;
  if (pl.blocker) return <span className="text-[9px] px-1.5 py-px rounded-full border bg-rose-50 text-rose-800 border-rose-300 flex items-center gap-1" data-testid={`map-ai-blocker-${pl.id}`}><AlertTriangle className="w-2.5 h-2.5" /> {pl.blocker}</span>;
  if (pl.will === "skip") return <span className="text-[9px] px-1.5 py-px rounded-full border bg-slate-100 text-slate-600 border-slate-300">{pl.note || "already there"}</span>;
  return (
    <>
      {pl.will === "one_off" && <span className="text-[9px] px-1.5 py-px rounded-full border bg-violet-50 text-violet-900 border-violet-300" title={pl.note} data-testid={`map-ai-oneoff-${pl.id}`}>one-off on this file</span>}
      {pl.forces_past?.length > 0 && <span className="text-[9px] px-1.5 py-px rounded-full border bg-amber-50 text-amber-900 border-amber-300 flex items-center gap-1" title="The file shows the track carried on while this sub-timeline was still running. Applying forces past it (Head of Legal) — it stays open on the map until resolved by hand." data-testid={`map-ai-forces-${pl.id}`}><AlertTriangle className="w-2.5 h-2.5" /> {pl.forces_past.join(", ")} still open — forces past it</span>}
      {pl.note && pl.will !== "one_off" && !pl.forces_past?.length && <span className="text-[9px] text-slate-500">{pl.note}</span>}
    </>
  );
}

export function LineCard({ group, picks, toggle, plan, edits, setEdit }) {
  const first = group.lines[0];
  const n = group.lines.length;
  const pickedCount = group.lines.filter((p) => picks.has(p.id)).length;
  const on = pickedCount === n;
  const pl0 = plan[first.id];
  const problem = first.problem;
  const tone = problem ? "border-rose-200 bg-rose-50/40" : on ? (pl0?.blocker ? "border-amber-300 bg-amber-50/40" : "border-[#3B70E0]/50 bg-[#3B70E0]/5") : "border-slate-200";
  return (
    <div className={`rounded border px-2.5 py-2 space-y-1 ${tone}`} data-testid={`map-ai-proposal-${first.id}`}>
      <div className="flex items-start gap-2">
        <Checkbox checked={on ? true : pickedCount ? "indeterminate" : false} disabled={!!problem} onCheckedChange={(v) => group.lines.forEach((p) => toggle(p.id, !!v))} className="mt-0.5" data-testid={`map-ai-tick-${first.id}`} />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            {on && pl0 && <span className="text-[9px] tabular-nums text-slate-400" data-testid={`map-ai-order-${first.id}`}>#{pl0.order}</span>}
            <span className="font-medium text-slate-900">{first.label}{n > 1 && <span className="ml-1.5 text-[10px] px-1.5 py-px rounded-full bg-slate-800 text-white" data-testid={`map-ai-repeats-${first.id}`}>×{n}</span>}</span>
            <span className="text-[10px] text-slate-500">{first.timeline_name}</span>
            <span className={`text-[9px] px-1.5 py-px rounded-full border ${CONF[first.confidence]}`} data-testid={`map-ai-confidence-${first.id}`}>{first.confidence}</span>
            {problem && <span className="text-[9px] px-1.5 py-px rounded-full border bg-rose-50 text-rose-800 border-rose-300 flex items-center gap-1" data-testid={`map-ai-problem-${first.id}`}><AlertTriangle className="w-2.5 h-2.5" /> {problem}</span>}
            {on && pl0?.depends_on && !pl0.blocker && <span className="text-[9px] px-1.5 py-px rounded-full border bg-slate-50 text-slate-600 border-slate-300 flex items-center gap-1" data-testid={`map-ai-depends-${first.id}`}><CornerDownRight className="w-2.5 h-2.5" /> after → {pl0.depends_on}</span>}
            {n === 1 && <Status pl={pl0} picked={on} />}
          </div>
          {first.reason && <div className="text-[11px] text-slate-700">{first.reason}</div>}
          {n === 1 ? (
            <div className="flex items-center gap-2 flex-wrap text-[11px]">
              <span className="text-slate-500">Date</span>
              <DateFix id={first.id} value={edits[first.id]?.date ?? first.date} hints={pl0?.blocker === "needs a date" ? pl0.date_hints : []} onPick={(d) => setEdit(first.id, { date: d })} />
              {Object.entries(first.refs || {}).map(([k, v]) => <span key={k} className="text-slate-500">{k.replace(/_/g, " ")} <span className="text-slate-800">{typeof v === "object" ? v?.name : String(v)}</span></span>)}
            </div>
          ) : (
            <ul className="space-y-0.5 text-[11px]" data-testid={`map-ai-repeat-list-${first.id}`}>
              {group.lines.map((p, i) => {
                const pl = plan[p.id];
                return (
                  <li key={p.id} className="flex items-center gap-2 flex-wrap">
                    <Checkbox checked={picks.has(p.id)} onCheckedChange={(v) => toggle(p.id, !!v)} className="h-3 w-3" data-testid={`map-ai-tick-${p.id}`} />
                    <span className="text-slate-500 w-5 tabular-nums">{i + 1}.</span>
                    <DateFix id={p.id} value={edits[p.id]?.date ?? p.date} hints={pl?.blocker === "needs a date" ? pl.date_hints : []} onPick={(d) => setEdit(p.id, { date: d })} compact />
                    {picks.has(p.id) && pl && <span className="text-[9px] text-slate-400">#{pl.order}</span>}
                    <Status pl={pl} picked={picks.has(p.id)} />
                  </li>
                );
              })}
            </ul>
          )}
          {first.evidence?.length > 0 && (
            <ul className="text-[10px] text-slate-500 space-y-0.5" data-testid={`map-ai-evidence-${first.id}`}>
              {first.evidence.map((e, i) => <li key={i}>“{e.quote}” <span className="text-slate-400">— {e.source}</span></li>)}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export function TrackGroup({ name, lines, picks, toggle, plan, edits, setEdit }) {
  return (
    <div className="space-y-1">
      <div className="text-[9px] text-slate-400 flex items-center gap-1 pl-0.5"><GitBranch className="w-2.5 h-2.5" /> {name}</div>
      {groupLines(lines).map((g) => <LineCard key={g.key} group={g} picks={picks} toggle={toggle} plan={plan} edits={edits} setEdit={setEdit} />)}
    </div>
  );
}

export function ResultRow({ r, edits, setEdit }) {
  const tone = !r.ok ? "border-rose-200 bg-rose-50/60" : r.forced?.length ? "border-amber-300 bg-amber-50/70" : r.skipped ? "border-slate-200 bg-slate-50" : r.one_off ? "border-violet-200 bg-violet-50/50" : "border-emerald-200 bg-emerald-50/60";
  return (
    <li className={`flex items-start gap-2 rounded border px-2.5 py-1.5 ${tone}`} data-testid={`map-ai-result-${r.id}`}>
      {!r.ok ? <AlertTriangle className="w-3.5 h-3.5 text-rose-700 mt-0.5 shrink-0" /> : r.forced?.length ? <AlertTriangle className="w-3.5 h-3.5 text-amber-700 mt-0.5 shrink-0" /> : <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${r.skipped ? "text-slate-400" : "text-emerald-700"}`} />}
      <span className="min-w-0 flex-1">
        {r.party && <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1.5" data-testid={`map-ai-result-party-${r.id}`}>{r.party}</span>}
        <span className="font-medium">{r.label}</span>
        {r.ok ? ` — ${r.summary || "recorded"}${r.created_events ? ` · ${r.created_events} calendar action${r.created_events === 1 ? "" : "s"}` : ""}` : ` — ${r.error}`}
        {r.one_off && <span className="block text-[10px] text-violet-800">Recorded as a one-off on this file (the process does not offer it on that arrow) — suggested to the Head of Legal in Legal settings.</span>}
        {r.forced?.length > 0 && <span className="block text-[10px] text-amber-800" data-testid={`map-ai-result-forced-${r.id}`}>Forced past {r.forced.join(", ")} — still open on the map; resolve it by hand when the file says how it ended.</span>}
        {!r.ok && r.needs_date && (
          <span className="block mt-1 text-[11px]"><span className="text-slate-500 mr-1">Date</span><DateFix id={r.id} value={edits[r.id]?.date} hints={r.date_hints} onPick={(d) => setEdit(r.id, { date: d })} /></span>
        )}
      </span>
    </li>
  );
}

export function ResultsList({ rounds, edits, setEdit, onRetry, busy }) {
  const last = rounds[rounds.length - 1] || [];
  const failed = last.filter((r) => !r.ok);
  return (
    <div className="space-y-2" data-testid="map-ai-results">
      {rounds.map((round, i) => (
        <ul key={i} className="space-y-1" data-testid={`map-ai-round-${i + 1}`}>
          {rounds.length > 1 && <li className="text-[9px] uppercase tracking-wider text-slate-400">Round {i + 1}</li>}
          {round.map((r) => <ResultRow key={`${i}-${r.id}`} r={r} edits={edits} setEdit={setEdit} />)}
        </ul>
      ))}
      {failed.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap pt-1 border-t">
          <span className="text-[11px] text-rose-800">{failed.length} line{failed.length === 1 ? "" : "s"} not applied — fix what each one says and retry; lines that depended on them are retried too.</span>
          <Button size="sm" variant="outline" className="h-7 text-[11px] ml-auto border-rose-300 text-rose-800 hover:bg-rose-50" disabled={busy} onClick={onRetry} data-testid="map-ai-retry">{busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}Retry the failed lines</Button>
        </div>
      )}
    </div>
  );
}
